import jsonExport from "jsonexport/dist";
import {
  downloadCSV,
  InfiniteListBase,
  useGetIdentity,
  useListContext,
  type Exporter,
} from "ra-core";
import { BulkActionsToolbar } from "@/components/admin/bulk-actions-toolbar";
import { BulkDeleteButton } from "@/components/admin/bulk-delete-button";
import { BulkExportButton } from "@/components/admin/bulk-export-button";
import { CreateButton } from "@/components/admin/create-button";
import { ExportButton } from "@/components/admin/export-button";
import { List } from "@/components/admin/list";
import { SelectAllButton } from "@/components/admin/select-all-button";
import { SortButton } from "@/components/admin/sort-button";
import { Card } from "@/components/ui/card";

import type { Company, Contact, Sale, Tag } from "../types";
import { BulkTagButton } from "./BulkTagButton";
import { ContactEmpty } from "./ContactEmpty";
import { ContactImportButton } from "./ContactImportButton";
import {
  ContactListContent,
  ContactListContentMobile,
} from "./ContactListContent";
import {
  ContactListFilterSummary,
  ContactListFilter,
} from "./ContactListFilter";
import { TopToolbar } from "../layout/TopToolbar";
import { InfinitePagination } from "../misc/InfinitePagination";
import MobileHeader from "../layout/MobileHeader";
import { MobileContent } from "../layout/MobileContent";

export const ContactList = () => {
  const { identity } = useGetIdentity();

  if (!identity) return null;

  return (
    <List
      title={false}
      actions={<ContactListActions />}
      perPage={25}
      sort={{ field: "last_seen", order: "DESC" }}
      exporter={exporter}
    >
      <ContactListLayoutDesktop />
    </List>
  );
};

const ContactListLayoutDesktop = () => {
  const { data, isPending, filterValues } = useListContext();

  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (isPending) return null;

  if (!data?.length && !hasFilters) return <ContactEmpty />;

  return (
    <div className="flex flex-row gap-8">
      <ContactListFilter />
      <div className="w-full flex flex-col gap-4">
        <Card className="py-0">
          <ContactListContent />
        </Card>
      </div>
      <BulkActionsToolbar>
        <ContactBulkActionButtons />
      </BulkActionsToolbar>
    </div>
  );
};

const ContactBulkActionButtons = () => (
  <>
    <SelectAllButton />
    <BulkTagButton />
    <BulkExportButton />
    <BulkDeleteButton />
  </>
);

const ContactListActions = () => (
  <TopToolbar>
    <SortButton fields={["first_name", "last_name", "last_seen"]} />
    <ContactImportButton />
    <ExportButton exporter={exporter} />
    <CreateButton />
  </TopToolbar>
);

export const ContactListMobile = () => {
  const { identity } = useGetIdentity();
  if (!identity) return null;

  return (
    <InfiniteListBase
      perPage={25}
      sort={{ field: "last_seen", order: "DESC" }}
      exporter={exporter}
      queryOptions={{
        onError: () => {
          /* Disable error notification as ContactListLayoutMobile handles it */
        },
      }}
    >
      <ContactListLayoutMobile />
    </InfiniteListBase>
  );
};

const ContactListLayoutMobile = () => {
  const { isPending, data, error, filterValues } = useListContext();

  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (!isPending && !data?.length && !hasFilters) return <ContactEmpty />;

  return (
    <div>
      <MobileHeader>
        <ContactListFilter />
      </MobileHeader>
      <MobileContent>
        <ContactListFilterSummary />
        <ContactListContentMobile />
        {!error && (
          <div className="flex justify-center">
            <InfinitePagination />
          </div>
        )}
      </MobileContent>
    </div>
  );
};

const exporter: Exporter<Contact> = async (records, fetchRelatedRecords) => {
  let companies: Record<string, Company> = {};
  let sales: Record<string, Sale> = {};
  let tags: Record<string, Tag> = {};

  try {
    const hasCompanyIds = records.some((c) => c.company_id != null);
    if (hasCompanyIds && fetchRelatedRecords) {
      companies = await fetchRelatedRecords<Company>(
        records,
        "company_id",
        "companies",
      );
    }
  } catch (err) {
    console.warn("Could not fetch companies for contacts export:", err);
  }

  try {
    const hasSalesIds = records.some((c) => c.sales_id != null);
    if (hasSalesIds && fetchRelatedRecords) {
      sales = await fetchRelatedRecords<Sale>(records, "sales_id", "sales");
    }
  } catch (err) {
    console.warn("Could not fetch sales for contacts export:", err);
  }

  try {
    const hasTags = records.some(
      (c) => Array.isArray(c.tags) && c.tags.length > 0,
    );
    if (hasTags && fetchRelatedRecords) {
      tags = await fetchRelatedRecords<Tag>(records, "tags", "tags");
    }
  } catch (err) {
    console.warn("Could not fetch tags for contacts export:", err);
  }

  const contacts = records.map((contact) => {
    const tIds = Array.isArray(contact.tags) ? contact.tags : [];
    const tagNames = tIds
      .map((tagId) => tags[String(tagId)]?.name || (tags as any)[tagId]?.name)
      .filter(Boolean)
      .join(", ");

    const sale =
      contact.sales_id != null
        ? sales[String(contact.sales_id)] || (sales as any)[contact.sales_id]
        : null;
    const company =
      contact.company_id != null
        ? companies[String(contact.company_id)] ||
          (companies as any)[contact.company_id]
        : null;

    const exportedContact = {
      ...contact,
      company: company?.name,
      empresa: company?.name,
      sales: sale
        ? `${sale.first_name || ""} ${sale.last_name || ""}`.trim()
        : undefined,
      responsavel: sale
        ? `${sale.first_name || ""} ${sale.last_name || ""}`.trim()
        : undefined,
      tags: tagNames,
      etiquetas: tagNames,
      email_work: contact.email_jsonb?.find((email) => email.type === "Work")
        ?.email,
      email_home: contact.email_jsonb?.find((email) => email.type === "Home")
        ?.email,
      email_other: contact.email_jsonb?.find((email) => email.type === "Other")
        ?.email,
      email_jsonb: JSON.stringify(contact.email_jsonb),
      email_fts: undefined,
      phone_work: contact.phone_jsonb?.find((phone) => phone.type === "Work")
        ?.number,
      phone_home: contact.phone_jsonb?.find((phone) => phone.type === "Home")
        ?.number,
      phone_other: contact.phone_jsonb?.find((phone) => phone.type === "Other")
        ?.number,
      phone_jsonb: JSON.stringify(contact.phone_jsonb),
      phone_fts: undefined,
    };
    delete exportedContact.email_fts;
    delete exportedContact.phone_fts;
    return exportedContact;
  });

  return jsonExport(contacts, {}, (_err: any, csv: string) => {
    downloadCSV("\uFEFF" + csv, "contatos");
  });
};
